// Pure graph + layout for the User Journey Sankey. No React, no fetching — so
// the numbers that end up on screen are unit-testable on their own.
//
// Input is the payload of GET /api/admin/journey (skilzBackend). Two units are
// kept apart and joined only at a dashed "seam":
//   left  — logged-in app SESSIONS (first screen → second screen)
//   right — USERS created in the window, through the signup steps to paid.
// Traffic source is not captured anywhere, so column 0 is a single grey
// "not tracked" node rather than an invented split.

export const RANGES = [7, 30, 90];
export const TOP = 8;

const STAGES = ['Traffic source', 'Landing screen', 'Next screen', 'Register', 'Profile set up', 'KYC L1', 'Paid'];
export const stageLabel = col => STAGES[col] || '';

export const pct = (part, whole) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—');
export const fmtInt = n => Number(n || 0).toLocaleString('en-US');

// Keep the top N rows by `value`, fold the rest into one "other (k)" bucket.
const topN = (rows, keyOf, n = TOP) => {
    const totals = new Map();
    rows.forEach(r => totals.set(keyOf(r), (totals.get(keyOf(r)) || 0) + r.sessions));
    const ranked = [...totals].sort((a, b) => b[1] - a[1]);
    const keep = new Set(ranked.slice(0, n).map(([k]) => k));
    const others = ranked.length - keep.size;
    return { key: k => (keep.has(k) ? k : '__other'), label: k => (k === '__other' ? `other (${others})` : k) };
};

export function buildJourneyGraph(data) {
    const nodes = new Map();
    const links = [];
    const node = (id, attrs) => {
        if (!nodes.has(id)) nodes.set(id, { id, inV: 0, outV: 0, drop: 0, value: 0, ...attrs });
        return nodes.get(id);
    };
    const link = (s, t, v, extra = {}) => {
        if (!(v > 0)) return;
        const existing = links.find(l => l.s === s && l.t === t);
        if (existing) existing.v += v;
        else links.push({ s, t, v, ...extra });
        nodes.get(s).outV += v;
        nodes.get(t).inV += v;
    };

    const sessions = (data && data.sessions) || { total: 0, entries: [], transitions: [] };
    const steps = (data && data.funnel && data.funnel.steps) || [];
    const hasSessions = sessions.total > 0;

    if (hasSessions) {
        node('src', { col: 0, label: 'Source not tracked', unit: 'sessions', kind: 'untracked' });
        const lp = topN(sessions.entries, r => r.page);
        sessions.entries.forEach(e => {
            const k = lp.key(e.page);
            node(`lp:${k}`, { col: 1, label: lp.label(k), unit: 'sessions', kind: 'page' });
            link('src', `lp:${k}`, e.sessions, { untracked: true });
        });
        const np = topN(sessions.transitions, r => r.to);
        sessions.transitions.forEach(t => {
            const from = `lp:${lp.key(t.from)}`;
            if (!nodes.has(from)) return; // landing beyond the API's top-50 cut
            const k = np.key(t.to);
            node(`np:${k}`, { col: 2, label: np.label(k), unit: 'sessions', kind: 'page' });
            link(from, `np:${k}`, t.sessions);
        });
        nodes.forEach(n => {
            if (n.col === 1) n.drop = Math.max(0, n.inV - n.outV);
        });
    }

    steps.forEach((s, i) => node(`f:${s.id}`, { col: 3 + i, label: s.label, unit: 'users', kind: 'step', value: s.count }));
    for (let i = 0; i < steps.length - 1; i++) {
        link(`f:${steps[i].id}`, `f:${steps[i + 1].id}`, steps[i + 1].count);
        nodes.get(`f:${steps[i].id}`).drop = Math.max(0, steps[i].count - steps[i + 1].count);
    }

    nodes.forEach(n => {
        n.value = Math.max(n.value, n.inV, n.outV + n.drop);
    });
    return { nodes: [...nodes.values()], links, hasSessions };
}

// Ranked drop-offs, biggest absolute loss first.
export function rankDropOffs(graph) {
    return graph.nodes
        .filter(n => n.drop > 0)
        .sort((a, b) => b.drop - a.drop || a.col - b.col)
        .map(n => ({
            id: n.id,
            stage: stageLabel(n.col),
            label: n.label,
            reached: n.value,
            dropped: n.drop,
            rate: pct(n.drop, n.value),
            unit: n.unit,
            method: n.kind === 'step'
                ? 'Reached this step but not the next (sequential, same cohort)'
                : 'Sessions whose first screen was this one and had no second screen',
        }));
}

// Column/row layout in SVG user units. Heights scale by value per column.
export function layoutJourney(graph, { colWidth = 168, nodeWidth = 10, pad = 16, top = 30, height = 420 } = {}) {
    const cols = 3 + Math.max(0, ...graph.nodes.filter(n => n.kind === 'step').map(n => n.col - 2));
    const byCol = Array.from({ length: Math.max(cols, 1) }, () => []);
    graph.nodes.forEach(n => byCol[n.col].push(n));
    byCol.forEach(c => c.sort((a, b) => b.value - a.value));
    // The two sides are different units (sessions vs users), so each side gets
    // its own scale: band thickness is comparable within a side, never across.
    const sideScale = from => {
        const scales = byCol
            .filter((c, i) => c.length && (from === 0 ? i < 3 : i >= 3))
            .map(c => (height - pad * (c.length - 1)) / Math.max(1, c.reduce((s, n) => s + n.value, 0)));
        return scales.length ? Math.min(...scales, 6) : 1;
    };
    const kSessions = sideScale(0);
    const kUsers = sideScale(3);
    const kOf = col => (col < 3 ? kSessions : kUsers);
    let H = top;
    byCol.forEach((c, i) => {
        let y = top;
        c.forEach(n => {
            n.k = kOf(i);
            n.x = 24 + i * colWidth;
            n.y = y;
            n.h = Math.max(2, n.value * n.k);
            y += n.h + pad + (n.drop ? 18 : 0);
        });
        H = Math.max(H, y);
    });
    const byId = new Map(graph.nodes.map(n => [n.id, n]));
    const outs = new Map();
    const ins = new Map();
    graph.links.forEach(l => {
        if (!outs.has(l.s)) outs.set(l.s, []);
        if (!ins.has(l.t)) ins.set(l.t, []);
        outs.get(l.s).push(l);
        ins.get(l.t).push(l);
    });
    outs.forEach((ls, id) => {
        let y = byId.get(id).y;
        ls.sort((a, b) => byId.get(a.t).y - byId.get(b.t).y).forEach(l => { l.w = Math.max(1, l.v * byId.get(id).k); l.sy = y; y += l.v * byId.get(id).k; });
    });
    ins.forEach((ls, id) => {
        let y = byId.get(id).y;
        ls.sort((a, b) => byId.get(a.s).y - byId.get(b.s).y).forEach(l => { l.ty = y; y += l.v * byId.get(id).k; });
    });
    graph.nodes.forEach(n => {
        const used = (outs.get(n.id) || []).reduce((s, l) => s + l.v * n.k, 0);
        n.dropY = n.y + Math.min(used, Math.max(0, n.h - Math.max(1, n.drop * n.k)));
        n.dropH = Math.max(1, n.drop * n.k);
    });
    const width = 24 + byCol.length * colWidth + 120;
    return { kSessions, kUsers, width, height: H + 10, cols: byCol.length, byId, nodeWidth, colWidth };
}

// Cubic band between two vertical edges.
export const bandPath = (x0, y0, x1, y1, w) => {
    const m = (x0 + x1) / 2;
    return `M${x0},${y0} C${m},${y0} ${m},${y1} ${x1},${y1} L${x1},${y1 + w} C${m},${y1 + w} ${m},${y0 + w} ${x0},${y0 + w} Z`;
};

export const dropPath = (x, y, w) =>
    `M${x},${y} C${x + 30},${y} ${x + 30},${y + 12} ${x + 46},${y + 12} L${x + 46},${y + 12 + w} C${x + 30},${y + 12 + w} ${x + 20},${y + w} ${x},${y + w} Z`;
